import { EVENT_STREAM_TIMEOUT_MS } from "./event-stream-visibility"

/**
 * Each stall of a live stream that had been delivering doubles the next live
 * stall timeout, at most this many times (7 s, 14 s, 28 s). On a link that
 * loses packets or drops out for seconds, reconnecting (a new TLS handshake
 * and a replay over the same link) costs more than waiting, and stalls again;
 * the price is finding a dead route that much later.
 */
const MAX_STALL_DOUBLINGS = 2
/** A link that has not stalled for this long is treated as healthy again. */
export const EVENT_STREAM_STALL_MEMORY_MS = 10 * 60_000
/** Network Information effective types slow enough to batch for. */
const SLOW_EFFECTIVE_TYPES = new Set(["slow-2g", "2g", "3g"])

type NetworkInformation = { effectiveType?: string; saveData?: boolean }

const browserNetwork = () =>
  typeof navigator === "undefined" ? undefined : (navigator as Navigator & { connection?: NetworkInformation }).connection

/** What an event stream has learned about the client's link to its machine. */
export function createEventStreamLink(input: { now?: () => number; network?: () => NetworkInformation | undefined } = {}) {
  const now = input.now ?? Date.now
  const network = input.network ?? browserNetwork
  let stalls = 0
  let stalledAt = 0
  const recentStalls = () => (stalls && now() - stalledAt < EVENT_STREAM_STALL_MEMORY_MS ? stalls : 0)
  return {
    /** A live stream that had been delivering went silent past its stall timeout. */
    stalled() {
      stalls = Math.min(recentStalls() + 1, MAX_STALL_DOUBLINGS)
      stalledAt = now()
    },
    /** How long a stream at this pace may stay silent before it counts as stalled. */
    stallTimeoutMs(pace: keyof typeof EVENT_STREAM_TIMEOUT_MS) {
      return pace === "live" ? EVENT_STREAM_TIMEOUT_MS.live * 2 ** recentStalls() : EVENT_STREAM_TIMEOUT_MS.idle
    },
    /**
     * The link stalled recently, or the browser reports it slow or saving data
     * (Chromium only), so the runtime should batch streamed text into fewer writes.
     */
    slow() {
      const info = network()
      return recentStalls() > 0 || info?.saveData === true || SLOW_EFFECTIVE_TYPES.has(info?.effectiveType ?? "")
    },
  }
}
